import React from 'react';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import type { i18n } from 'i18next';
import { bindActionCreators, Dispatch } from 'redux';
import CollapsibleText from './CollapsibleText';
import LatestNPublicRecords from './LatestNPublicRecords';
import { IAppState } from '../redux/store/types';
import { IRecord, ViewMode } from '../redux/records/types';
import { UserType } from '../redux/auth/types';
import {
  addRecordAction,
  setCurrentRecordAction,
  prepareDeleteRecordCommandAction,
  deleteRecordAction,
  deleteRecordAndReadNextAction,
  removeLatestPublicRecordAction,
  setViewModeAction,
  toggleExpandCondensedViewModeAction,
  updateRecordFieldAction,
  setRecordFormErrorsAction,
  resetRecordFormAction,
  prepareCreateRecordCommandAction,
  prepareLinkRecordCommandAction,
  prepareUnlinkRecordCommandAction,
  prepareReadRecordsCommandAction,
  resetRecordsAction,
  setShowSummariesAction,
  setShowAllLinkableAction,
} from '../redux/records/actions';
import { clearSubrecordAction, prepareReadSubrecordsCommandAction } from '../redux/subrecords/actions';
import { Trash2, Pencil, TextCursorInput, Copy, Link, Unlink, Plus, List, Expand, ListCollapse } from 'lucide-react';
import { timeShortDisplay } from '../utils/utils';
import { RecordForm } from './RecordForm';
import { RecordSearch } from './RecordSearch';
import RecordSummariesList from './RecordSummariesList';
import Subrecords from './Subrecords';
import { ISubrecord } from 'redux/subrecords/types';
import { ISummary } from 'redux/summaries/types';
import { toast } from 'react-toastify';
import { ILLMProfile } from 'redux/llmProfiles/types';
import { v4 as uuidv4 } from 'uuid';
import Spinner from './Spinner';
import { RecordAccessLevel } from 'data-types';

/**
 * Component for displaying and managing records
 * @class Records
 */
class Records extends React.Component<IRecordsProps> {
  /**
   * Renders the component
   * @returns {JSX.Element} The rendered component
   */
  render() {
    const { isWsConnected, viewMode, setViewModeAction, t, records, showAllLinkable, searchText } = this.props;
    const showRecordForm = viewMode === ViewMode.SHOW_FORM;
    const showRecordSearch = viewMode === ViewMode.SHOW_SEARCH;

    return (
      <div className={`page records-container body-container${!isWsConnected ? ' disconnected' : ''}`}>
        {showRecordSearch && <RecordSearch />}
        <header className='header'>
          <span className='title'>
            {showRecordForm
              ? t('records.chat')
              : t('records.chatsCount', {
                  count: records.length,
                  similarity: (searchText && `${t('records.similar')} `) || '',
                  publicityLevel: showAllLinkable ? t('records.linkable') : t('records.privateAndLinked'),
                })}
          </span>
          <div className={`actions ${(showRecordForm && 'form-displayed') || ''}`}>
            <button
              onClick={this.handleToggleAllLinkable}
              className={`action-button link ${(showAllLinkable && 'show-all-linkable') || ''}`}
              title={t('records.showAllLinkable')}
            >
              <Link />
            </button>
            {!showRecordForm && (
              <button onClick={() => setViewModeAction(ViewMode.SHOW_FORM)} className='action-button add' title={t('records.add')}>
                {<Plus />}
              </button>
            )}
          </div>
        </header>
        <div className='data-container records-list'>
          {showRecordForm ? (
            <>
              <RecordForm />
              <LatestNPublicRecords />
            </>
          ) : records.length > 0 ? (
            records /*.slice(0, 1)*/
              .map((record) => this.renderRecord(record))
          ) : (
            !showRecordSearch && <Spinner />
          )}
        </div>

        {/* <CollapsibleText
          ownerRecordId='1'
          userText='-'
          aiText='## SQL join types Venn diagrams  - A = left table; B = right table - Highlighted area = rows returned    ### INNER JOIN <svg width="260" height="160" viewBox="0 0 260 160" role="img" aria-label="INNER JOIN: only the overlap between A and B"> <title>INNER JOIN</title> <defs> <clipPath id="clipA"> <circle cx="100" cy="85" r="55"/> </clipPath> <clipPath id="clipB"> <circle cx="160" cy="85" r="55"/> </clipPath> </defs> <g clip-path="url(#clipA)"> <rect clip-path="url(#clipB)" x="0" y="0" width="260" height="160" fill="#4f8ef7" fill-opacity="0.6"/> </g> <circle cx="100" cy="85" r="55" fill="none" stroke="#555" stroke-width="2"/> <circle cx="160" cy="85" r="55" fill="none" stroke="#555" stroke-width="2"/> <text x="100" y="145" text-anchor="middle" font-family="sans-serif" font-size="12">A</text> <text x="160" y="145" text-anchor="middle" font-family="sans-serif" font-size="12">B</text> <text x="130" y="20" text-anchor="middle" font-family="sans-serif" font-size="14" font-weight="bold">INNER JOIN</text> </svg> ### LEFT OUTER JOIN <svg width="260" height="160" viewBox="0 0 260 160" role="img" aria-label="LEFT OUTER JOIN: all of A, including overlap with B"> <title>LEFT OUTER JOIN</title> <circle cx="100" cy="85" r="55" fill="#4f8ef7" fill-opacity="0.6"/> <circle cx="100" cy="85" r="55" fill="none" stroke="#555" stroke-width="2"/> <circle cx="160" cy="85" r="55" fill="none" stroke="#555" stroke-width="2"/> <text x="100" y="145" text-anchor="middle" font-family="sans-serif" font-size="12">A</text> <text x="160" y="145" text-anchor="middle" font-family="sans-serif" font-size="12">B</text> <text x="130" y="20" text-anchor="middle" font-family="sans-serif" font-size="14" font-weight="bold">LEFT OUTER JOIN</text> </svg> ### RIGHT OUTER JOIN <svg width="260" height="160" viewBox="0 0 260 160" role="img" aria-label="RIGHT OUTER JOIN: all of B, including overlap with A"> <title>RIGHT OUTER JOIN</title> <circle cx="160" cy="85" r="55" fill="#4f8ef7" fill-opacity="0.6"/> <circle cx="100" cy="85" r="55" fill="none" stroke="#555" stroke-width="2"/> <circle cx="160" cy="85" r="55" fill="none" stroke="#555" stroke-width="2"/> <text x="100" y="145" text-anchor="middle" font-family="sans-serif" font-size="12">A</text> <text x="160" y="145" text-anchor="middle" font-family="sans-serif" font-size="12">B</text> <text x="130" y="20" text-anchor="middle" font-family="sans-serif" font-size="14" font-weight="bold">RIGHT OUTER JOIN</text> </svg> ### FULL OUTER JOIN <svg width="260" height="160" viewBox="0 0 260 160" role="img" aria-label="FULL OUTER JOIN: all of A and all of B"> <title>FULL OUTER JOIN</title> <circle cx="100" cy="85" r="55" fill="#4f8ef7" fill-opacity="0.6"/> <circle cx="160" cy="85" r="55" fill="#4f8ef7" fill-opacity="0.6"/> <circle cx="100" cy="85" r="55" fill="none" stroke="#555" stroke-width="2"/> <circle cx="160" cy="85" r="55" fill="none" stroke="#555" stroke-width="2"/> <text x="100" y="145" text-anchor="middle" font-family="sans-serif" font-size="12">A</text> <text x="160" y="145" text-anchor="middle" font-family="sans-serif" font-size="12">B</text> <text x="130" y="20" text-anchor="middle" font-family="sans-serif" font-size="14" font-weight="bold">FULL OUTER JOIN</text> </svg> ### CROSS JOIN (Cartesian product) <svg width="260" height="160" viewBox="0 0 260 160" role="img" aria-label="CROSS JOIN: every row of A paired with every row of B"> <title>CROSS JOIN (Cartesian product)</title> <rect x="30" y="35" width="200" height="90" rx="6" ry="6" fill="none" stroke="#555" stroke-width="2"/> <g fill="#4f8ef7" fill-opacity="0.6" stroke="#555" stroke-width="0.5"> <!-- draw a 4x4 grid of cells to represent all combinations --> <rect x="30" y="35" width="50" height="22.5"/> <rect x="80" y="35" width="50" height="22.5"/> <rect x="130" y="35" width="50" height="22.5"/> <rect x="180" y="35" width="50" height="22.5"/> <rect x="30" y="57.5" width="50" height="22.5"/> <rect x="80" y="57.5" width="50" height="22.5"/> <rect x="130" y="57.5" width="50" height="22.5"/> <rect x="180" y="57.5" width="50" height="22.5"/> <rect x="30" y="80" width="50" height="22.5"/> <rect x="80" y="80" width="50" height="22.5"/> <rect x="130" y="80" width="50" height="22.5"/> <rect x="180" y="80" width="50" height="22.5"/> <rect x="30" y="102.5" width="50" height="22.5"/> <rect x="80" y="102.5" width="50" height="22.5"/> <rect x="130" y="102.5" width="50" height="22.5"/> <rect x="180" y="102.5" width="50" height="22.5"/> </g> <text x="30" y="28" text-anchor="start" font-family="sans-serif" font-size="12">A rows</text> <text x="230" y="28" text-anchor="end" font-family="sans-serif" font-size="12">B rows</text> <text x="130" y="20" text-anchor="middle" font-family="sans-serif" font-size="14" font-weight="bold">CROSS JOIN</text> </svg> Notes: - Many databases do not support FULL OUTER JOIN directly (e.g., SQLite); you can emulate it with UNION of LEFT and RIGHT joins plus filters. - RIGHT JOIN can be emulated by swapping table order and using LEFT JOIN.'
        /> */}
      </div>
    );
  }

  /**
   * Renders a single record with its subrecords/summaries
   * @param {IRecord} record - The record to render
   * @returns {JSX.Element} The rendered record
   */
  renderRecord = (record: IRecord): JSX.Element => {
    const { userId, viewMode, currentRecordId, llmProfiles, showSummaries, showAllLinkable, t } = this.props;
    const isLinkable = showAllLinkable && record.saas_tenant_id !== userId;
    const isUnLinkable = !showAllLinkable && record.saas_tenant_id !== userId;
    const showCondensedView = viewMode === ViewMode.SHOW_CONDENSED_RECORDS_VIEW || currentRecordId !== record.record_id;
    const allowModifications = record.saas_tenant_id === userId && record.record_id === currentRecordId;
    const currentRecordClassName = record.record_id === currentRecordId ? ' current' : '';

    /**
     * Handles record selection and loads its subrecords
     * @param {string} record_id - ID of the selected record
     */
    const handleRecordClick = (record_id: string) => {
      if (record_id !== this.props.currentRecordId) {
        this.props.setCurrentRecordAction(record_id);
        this.props.prepareReadRecordsCommandAction({ record_id });
      }
    };

    return (
      <div
        key={record.record_id}
        className={`record-and-subrecords-container ${!showCondensedView && record.record_id === currentRecordId ? 'expanded' : 'condensed'} ${
          (record.is_archived && 'archived') || ''
        } ${currentRecordClassName} ${(record.subrecordsCount === 0 && 'no-subrecords') || ''}`}
      >
        <div
          className={`table-row record ${currentRecordClassName} ${record.onroute ? ' onroute' : ''} open ${
            record.access_level !== RecordAccessLevel.Private ? (record.saas_tenant_id === userId || showAllLinkable ? ' is-public' : 'is-linked') : ''
          } ${(isLinkable ? 'linkable' : isUnLinkable && 'unlinkable') || ''}`}
          onClick={() => handleRecordClick(record.record_id)}
          title={(record.access_level === RecordAccessLevel.Public && t('records.publicChat')) || ''}
        >
          <div className='updated-at' data-title={t('common.fields.updatedAt')} title={record.record_id}>
            {record.updated_at ? timeShortDisplay(new Date(record.updated_at)) : '. . .'}
            {record.similarity_score !== undefined && record.similarity_score !== null && Number.isFinite(Number(record.similarity_score)) ? (
              <span className='similarity-score' title={t('common.tooltips.similarityScore')}>{`${Number(record.similarity_score).toFixed(2)} `}</span>
            ) : (
              ''
            )}
            <span className='saas-user-name'>{record.saas_tenant_name}</span>
            {!record.onroute && (
              <>
                <button onClick={this.props.toggleExpandCondensedViewModeAction} className='action-button expand-collapse'>
                  {showCondensedView ? <Expand /> : <ListCollapse />}
                  <span className={`subrecords-count ${((record.subrecordsCount as number) > 0 && 'is_positive') || ''}`}>{record.subrecordsCount}</span>
                </button>
                <div className='actions'>
                  {!showCondensedView && allowModifications && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        this.handleEditRecord(record);
                      }}
                      className='action-button edit'
                      title={t('common.edit')}
                    >
                      <Pencil />
                    </button>
                  )}
                  {!showCondensedView && allowModifications && (
                    <button className='action-button' title={t('common.tooltips.duplicate')} onClick={() => this.handleDuplicateRecord(record)}>
                      <Copy />
                    </button>
                  )}
                  {isLinkable ? (
                    <button onClick={() => this.handleLinkRecord(record)} className='action-button link' title={t('records.linkWithDescription')}>
                      <Link />
                    </button>
                  ) : (
                    isUnLinkable && (
                      <button onClick={() => this.handleUnlinkRecord(record)} className='action-button unlink' title={t('records.unlinkWithDescription')}>
                        <Unlink />
                      </button>
                    )
                  )}
                  {!showCondensedView && allowModifications && record.is_archived && (
                    <button onClick={() => this.handleDeleteRecord(record)} className='action-button delete' title={t('common.delete')}>
                      <Trash2 />
                    </button>
                  )}
                  <button
                    onClick={() => this.handleToggleSummariesAndSubrecords(record.record_id)}
                    className={`action-button ${!showSummaries ? 'summaries' : 'subrecords'}`}
                    title={t('records.tooltips.toggleView', {
                      from: showSummaries ? t('summaries.title') : t('subrecords.title'),
                      to: showSummaries ? t('subrecords.title') : t('summaries.title'),
                    })}
                  >
                    {showSummaries ? <TextCursorInput /> : <List />}
                  </button>
                </div>
              </>
            )}
            <span className='llm'>{llmProfiles?.find((p) => p.profile_id === record.llm_profile_id)?.model_id}</span>
          </div>

          <CollapsibleText ownerRecordId={record.record_id} userText={record.user_text} aiText={record.ai_text} interim={record.interim} />
        </div>

        {record.record_id === currentRecordId && (showSummaries ? <RecordSummariesList /> : <Subrecords />)}
      </div>
    );
  };

  /**
   * Toggles between summaries and subrecords view
   */
  handleToggleSummariesAndSubrecords = (record_id: string) => {
    const prevShowSummaries = this.props.showSummaries;
    this.props.setShowSummariesAction(!prevShowSummaries);
    if (prevShowSummaries) this.props.prepareReadSubrecordsCommandAction(record_id);
    this.props.setViewModeAction(ViewMode.SHOW_CONDENSED_RECORDS_VIEW);
  };

  /**
   * Sets up record for editing
   * @param {IRecord} record - The record to edit
   */
  handleEditRecord = (record: IRecord) => {
    const { setViewModeAction, updateRecordFieldAction } = this.props;

    updateRecordFieldAction('id', record.record_id);
    updateRecordFieldAction('user_text', record.user_text);
    updateRecordFieldAction('llm_profile_id', record.llm_profile_id);
    updateRecordFieldAction('access_level', record.access_level !== undefined ? record.access_level : RecordAccessLevel.Private);
    updateRecordFieldAction('is_archived', record.is_archived || false);
    updateRecordFieldAction('retainCurrentAccessLevel', record.retainCurrentAccessLevel === true);
    updateRecordFieldAction('shareWithSaasTenantIds', record.sharedSaasTenantIdsAndNames?.map((t) => t.id) || []);
    setViewModeAction(ViewMode.SHOW_FORM);
  };

  /**
   * Duplicates an record
   * @param {ISummary} doc - Summary to duplicate
   */
  handleDuplicateRecord = async (record: IRecord) => {
    const record_id = uuidv4();
    const accessLevel = record.access_level !== undefined ? record.access_level : RecordAccessLevel.Private;
    this.props.prepareCreateRecordCommandAction(record_id, record.user_text, record.ai_text, record.llm_profile_id, accessLevel);
    this.props.addRecordAction({ ...record, record_id, ai_text: '', onroute: true }); // ai_text remains '' until the backend confirms the insertion, to signal 'work in progress'...
    this.props.setCurrentRecordAction(record_id);
  };

  /**
   * Deletes an record after confirmation
   * @param {IRecord} record - The record to delete
   */
  handleDeleteRecord = (record: IRecord) => {
    const { t, currentRecordSummaries } = this.props;

    // TODO: How to handle references from pinecone to records that were deleted? .. (include cascading delete!) - for the time being, a record with subrecords cannot be deleted.
    if (currentRecordSummaries.length > 0 || (record.subrecordsCount as number) > 0) toast.warn(t('records.warnDeleteWithData'));
    else if (window.confirm(t('records.confirmDelete'))) {
      this.props.prepareDeleteRecordCommandAction(record.record_id, record.saas_tenant_id as string);
      this.props.deleteRecordAndReadNextAction(record.record_id);
      this.props.removeLatestPublicRecordAction(record.record_id);
    }
  };

  /**
   * Handle click on the toggle button between all linkable records.
   */
  handleToggleAllLinkable = () => {
    const isTogglingToAllLinkable = !this.props.showAllLinkable;
    this.props.resetRecordsAction();
    this.props.prepareReadRecordsCommandAction({ readAllLinkable: isTogglingToAllLinkable, searchText: this.props.searchText });
    this.props.setShowAllLinkableAction(isTogglingToAllLinkable);
    if (isTogglingToAllLinkable) this.props.setViewModeAction(ViewMode.SHOW_CONDENSED_RECORDS_VIEW);
  };

  /**
   * Handle linking a public record to the current user.
   */
  handleLinkRecord(record: IRecord): void {
    const { record_id, saas_tenant_id } = record;
    this.props.prepareLinkRecordCommandAction(record_id, saas_tenant_id as string);
    this.props.resetRecordsAction();
    this.props.setShowAllLinkableAction(false);
    this.props.addRecordAction({ ...record, ai_text: '', onroute: true }); // ai_text remains '' until the backend confirms the insertion, to signal 'work in progress'...
    setTimeout(() => this.props.prepareReadRecordsCommandAction({ readAllLinkable: false, searchText: this.props.searchText }), 500);
  }

  /**
   * Handle unlinking a public record from the current user.
   */
  handleUnlinkRecord(record: IRecord): void {
    const { record_id, saas_tenant_id } = record;
    this.props.prepareUnlinkRecordCommandAction({ record_id, saas_tenant_id: saas_tenant_id as string });
    this.props.deleteRecordAction(record_id);
  }
}

/**
 * Props interface for Records component
 * @interface IRecordsProps
 */
interface IRecordsProps {
  t: (key: string, options?: any) => string;
  i18n: i18n;
  tReady: boolean;
  isWsConnected: boolean;
  userType: UserType;
  userId: string | null;
  records: IRecord[];
  subrecords: Record<string, ISubrecord[]>;
  llmProfiles: ILLMProfile[];
  currentRecordId: string | null;
  currentRecordSummaries: ISummary[];
  viewMode: ViewMode;
  showSummaries: boolean;
  showAllLinkable: boolean;
  searchText: string;
  clearSubrecordAction: typeof clearSubrecordAction;
  prepareReadSubrecordsCommandAction: typeof prepareReadSubrecordsCommandAction;
  addRecordAction: typeof addRecordAction;
  setCurrentRecordAction: typeof setCurrentRecordAction;
  prepareDeleteRecordCommandAction: typeof prepareDeleteRecordCommandAction;
  deleteRecordAction: typeof deleteRecordAction;
  deleteRecordAndReadNextAction: typeof deleteRecordAndReadNextAction;
  removeLatestPublicRecordAction: typeof removeLatestPublicRecordAction;
  setViewModeAction: typeof setViewModeAction;
  toggleExpandCondensedViewModeAction: typeof toggleExpandCondensedViewModeAction;
  setShowSummariesAction: typeof setShowSummariesAction;
  setShowAllLinkableAction: typeof setShowAllLinkableAction;
  updateRecordFieldAction: typeof updateRecordFieldAction;
  setRecordFormErrorsAction: typeof setRecordFormErrorsAction;
  resetRecordFormAction: typeof resetRecordFormAction;
  prepareCreateRecordCommandAction: typeof prepareCreateRecordCommandAction;
  prepareLinkRecordCommandAction: typeof prepareLinkRecordCommandAction;
  prepareUnlinkRecordCommandAction: typeof prepareUnlinkRecordCommandAction;
  resetRecordsAction: typeof resetRecordsAction;
  prepareReadRecordsCommandAction: typeof prepareReadRecordsCommandAction;
}

// Maps required state from Redux store to component props
const mapStateToProps = (state: IAppState) => ({
  userType: state.auth.userType,
  userId: state.auth.userId,
  isWsConnected: state.websockets.isConnected,
  records: state.records.records,
  subrecords: state.subrecords.subrecords,
  currentRecordId: state.records.currentRecordId,
  currentRecordSummaries: state.summaries.summaries,
  viewMode: state.records.viewMode,
  showSummaries: state.records.showSummaries,
  showAllLinkable: state.records.showAllLinkable,
  searchText: state.records.searchText,
  llmProfiles: state.llmProfiles.llmProfiles,
});

// Map Redux actions to component props
const mapDispatchToProps = (dispatch: Dispatch) =>
  bindActionCreators(
    {
      clearSubrecordAction,
      prepareReadSubrecordsCommandAction,
      addRecordAction,
      setCurrentRecordAction,
      prepareDeleteRecordCommandAction,
      deleteRecordAction,
      deleteRecordAndReadNextAction,
      removeLatestPublicRecordAction,
      setViewModeAction,
      toggleExpandCondensedViewModeAction,
      setShowSummariesAction,
      setShowAllLinkableAction,
      updateRecordFieldAction,
      setRecordFormErrorsAction,
      resetRecordFormAction,
      prepareCreateRecordCommandAction,
      prepareLinkRecordCommandAction,
      prepareUnlinkRecordCommandAction,
      resetRecordsAction,
      prepareReadRecordsCommandAction,
    },
    dispatch,
  );

export default connect(mapStateToProps, mapDispatchToProps)(withTranslation()(Records));
